/**
 * MPS
 * usePaymentFlow - Reusable payment orchestration hook
 * Supports card, Apple Pay, Google Pay (Braintree v2), PayPal
 *
 * Device Compatibility Flow:
 * 1. On mount, this hook detects device capabilities:
 *    - Apple Pay: window.ApplePaySession.canMakePayments()
 *    - Google Pay: paymentsClient.isReadyToPay()
 * 2. Sets canUseApplePay and canUseGooglePay state
 * 3. Page component (Step5PaymentMethod) sends device info to backend:
 *    - GET /methods?type=onetime&applepay=true&googlepay=true
 * 4. Backend responds with available payment methods based on:
 *    - Device capabilities (from query params)
 *    - Merchant configuration
 *    - Backend can disable wallets by returning empty methods list
 * 5. Only available methods are shown to user
 * 6. On method selection, Braintree client is initialized for that method
 * 7. Payment processing uses method-specific tokenization (nonce)
 *
 * All imports are from braintree-web npm package:
 * - googlePayment: braintree-web/google-payment
 * - applePay: braintree-web/apple-pay
 * - hosted-fields, threeDS: braintree-web via shared service modules
 * Wallet detection uses browser APIs loaded from CDN scripts.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useBraintree } from '@context/Braintree';
import { useA11y } from '@context/Utils/A11y';
import {
  createHostedFields,
  tokenizeHostedFields,
  teardownHostedFields,
  validateAllFields,
  setupFieldListeners
} from '@services/braintree/hosted-fields';
import threeDS from '@services/braintree/threeDS';
import paymentService from '@services/payment';
import googlePayment from 'braintree-web/google-payment';
import applePay from 'braintree-web/apple-pay';

// REFACTOR later: move to global constants or payment constants
// ----------------------------
// Payment status constants
// ----------------------------
export const PAYMENT_STATUS = {
  IDLE: 'idle',
  INITIALIZING: 'initializing',
  READY: 'ready',
  VALIDATING: 'validating',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
  REDIRECTING: 'redirecting'
};

// ----------------------------
// Environment config
// ----------------------------
const GOOGLE_PAY_ENV = process.env.REACT_APP_GOOGLE_PAY_ENV;
const GOOGLE_PAY_MERCHANT_ID = process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID;
// const GOOGLE_PAY_MERCHANT_NAME = process.env.REACT_APP_GOOGLE_PAY_MERCHANT_NAME;
const BRAINTREE_ENV = process.env.REACT_APP_BRAINTREE_ENV;

// ----------------------------
// Apple Pay Configuration
// ----------------------------
// Merchant identifier should match the one configured in Braintree dashboard
const APPLE_PAY_MERCHANT_LABEL =
  process.env.REACT_APP_APPLE_PAY_MERCHANT_LABEL || 'Your Company Name';
const APPLE_PAY_COUNTRY_CODE = 'DE';
const APPLE_PAY_CURRENCY = 'EUR';

// ----------------------------
// Utility: Detect if running in iframe
// ----------------------------
function isRunningInIframe() {
  return window.self !== window.top;
}

// ----------------------------
// Google Pay Configuration (Phase 1: Early Signal)
// ----------------------------
// Used during device capability detection (runs immediately on mount)
// This provides a fast, approximate signal for the UI.
// Phase 2 (payment processing) uses Braintree's authoritative config.
//
// Reference: https://developers.google.com/pay/api/web/reference/request-objects
const GOOGLE_PAY_BASE_REQUEST = {
  apiVersion: 2,
  apiVersionMinor: 0,
  allowedPaymentMethods: [
    {
      type: 'CARD',
      parameters: {
        // PAN_ONLY: User's saved card (Primary Account Number only)
        // CRYPTOGRAM_3DS: 3D Secure tokenized card (more secure)
        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
        allowedCardNetworks: ['AMEX', 'MASTERCARD', 'VISA'] // From Braintree config
      }
    }
  ]
};

export function usePaymentFlow(options = {}) {
  const { flow = 'onetime', onSuccess, onError } = options;
  const { client, initialize: initBraintree, cleanup: cleanupBraintree } = useBraintree();
  const { tA11yTranslate } = useA11y();

  // DEBUG: Log the flow parameter received
  // console.log('[usePaymentFlow] INIT - received options:', options);
  // console.log('[usePaymentFlow] INIT - flow parameter:', flow);

  // ----------------------------
  // State
  // ----------------------------
  const [status, setStatus] = useState(PAYMENT_STATUS.IDLE);
  const [error, setError] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [fieldValidity, setFieldValidity] = useState({
    number: false,
    cvv: false,
    expirationDate: false
  });
  const [canUseApplePay, setCanUseApplePay] = useState(false);
  const [canUseGooglePay, setCanUseGooglePay] = useState(false);

  // ----------------------------
  // Refs
  // ----------------------------
  const hostedFieldsRef = useRef(null);
  const googlePayInstanceRef = useRef(null);
  const applePayInstanceRef = useRef(null);
  const paymentsClientRef = useRef(null);
  const clientRef = useRef(null);
  const isMountedRef = useRef(true);

  // ----------------------------
  // Sync Braintree client
  // ----------------------------
  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  // ----------------------------
  // Apple Pay detection
  // ----------------------------
  // Detects device capability using browser native API
  // Only available on Safari/iOS devices that support Apple Pay
  useEffect(() => {
    let cancelled = false;
    const detectApplePay = async () => {
      try {
        // console.log('[ApplePay] Detection starting...');
        // console.log('[ApplePay] window.ApplePaySession:', typeof window.ApplePaySession);

        // NOTE about iframe and applepay:
        // Iframe support is kept for potential future use. Apple Pay has known issues
        // with cross-origin iframes due to browser security restrictions (see lines 912-918, 990-996, 1036-1039).
        // This code can be removed if iframe-based payments are permanently discontinued.

        // CRITICAL: Check if running in iframe (cross-origin iframes have restrictions)
        const inIframe = isRunningInIframe();
        // console.log('[ApplePay] Running in iframe:', inIframe);

        if (inIframe) {
          // Try to detect cross-origin restrictions
          try {
            // Attempt to access window.top - will throw SecurityError if cross-origin
            // eslint-disable-next-line no-unused-expressions
            window.top.location.href;
            // console.log('[ApplePay] iframe is SAME-ORIGIN (can access parent)');
          } catch (securityErr) {
            // console.warn('[ApplePay] iframe is CROSS-ORIGIN (cannot access parent)');
            // console.warn('[ApplePay] Apple Pay may not work in cross-origin iframe');
            // console.warn('[ApplePay] SecurityError:', securityErr.message);
          }

          // Check if iframe has payment permission
          try {
            const hasPaymentPermission = document.featurePolicy?.allowsFeature('payment');
            // console.log('[ApplePay] iframe has payment permission:', hasPaymentPermission);
          } catch (fpErr) {
            console.warn('[ApplePay] Could not check payment permission');
          }
        }

        // Apple Pay only available on Safari with compatible device
        if (window.ApplePaySession?.canMakePayments) {
          try {
            const canPay = await window.ApplePaySession.canMakePayments();
            if (!cancelled) {
              // console.log('[ApplePay] Device capability detected:', canPay);

              // Additional warning for iframe context
              if (inIframe && canPay) {
                // console.warn('[ApplePay] Device supports Apple Pay BUT running in iframe');
                // console.warn(
                //   '[ApplePay] Payment sheet may close immediately due to iframe restrictions'
                // );
              }

              setCanUseApplePay(canPay);
            }
          } catch (err) {
            console.warn('[ApplePay] canMakePayments check failed');
            if (!cancelled) setCanUseApplePay(false);
          }
        } else {
          // console.warn('[ApplePay] Apple Pay not available on this device/browser');
          if (!cancelled) setCanUseApplePay(false);
        }
      } catch (err) {
        const errorMsg = err?.message || String(err) || 'Unknown error';
        // console.warn('[ApplePay] Detection error:', errorMsg);
        if (!cancelled) setCanUseApplePay(false);
      }
    };
    detectApplePay();
    return () => {
      cancelled = true;
    };
  }, []);

  // ----------------------------
  // Google Pay detection (UI-only, no Braintree init)
  // ----------------------------
  // Detects device capability using Google Pay API script (loaded in index.html)
  // Results sent to backend via /methods endpoint to let backend decide
  // which payment methods to offer (may be restricted by merchant config)
  // DEV: Can be forced with REACT_APP_FORCE_GOOGLE_PAY=true for testing on non-supported devices

  // !!!! NOTE IMPORTANT: don't delete this block of code !!!
  // Google Pay detection ENABLED (temporary only in feature branch and deploy to psa)
  useEffect(() => {
    let cancelled = false;
    const detectGooglePay = async () => {
      try {
        // Google Pay script loads window.google.payments.api globally
        // If not available, Google Pay is not supported on this device
        if (window.google?.payments?.api?.PaymentsClient) {
          let paymentsClient;
          try {
            paymentsClient = new window.google.payments.api.PaymentsClient({
              environment: GOOGLE_PAY_ENV
            });
            paymentsClientRef.current = paymentsClient;
            // console.log('[GooglePay] PaymentsClient created successfully');
            // console.log('[GooglePay] PaymentsClient environment:', GOOGLE_PAY_ENV);
          } catch (clientErr) {
            throw new Error(
              `Failed to create PaymentsClient: ${clientErr?.message || String(clientErr)}`
            );
          }

          // PHASE 1: Early Signal - Device Capability Detection
          // Check if device supports card-based Google Pay using standard config
          // This is fast and provides a UI hint, but not the authoritative answer
          // Phase 2 (in payment processing) will use Braintree's config for actual validation
          const isReady = await paymentsClient.isReadyToPay(GOOGLE_PAY_BASE_REQUEST);

          if (!cancelled) {
            // Device capability detection
            const isSupported = isReady?.result === true;
            // console.log('[GooglePay] Device capability detected:', isSupported);
            setCanUseGooglePay(isSupported);
          }
        } else if (!cancelled) {
          // Script not loaded means Google Pay not available on this device
          console.warn('[GooglePay] Google Pay script not loaded or not available on this device');
          setCanUseGooglePay(false);
        }
      } catch (err) {
        const errorMsg = err?.message || String(err) || 'Unknown error';
        console.warn('[GooglePay] Detection failed');
        // console.warn('[GooglePay] Error object:', err);
        // console.warn('[GooglePay] window.google available?', !!window.google);
        if (!cancelled) setCanUseGooglePay(false);
      }
    };
    detectGooglePay();
    return () => {
      cancelled = true;
    };
  }, []);

  // ----------------------------
  // Cleanup on unmount
  // ----------------------------
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (hostedFieldsRef.current) teardownHostedFields(hostedFieldsRef.current);
      googlePayInstanceRef.current = null;
      paymentsClientRef.current = null;
    };
  }, []);

  // ----------------------------
  // Select payment method
  // ----------------------------
  const selectPaymentMethod = useCallback(
    async (method) => {
      if (!isMountedRef.current) return;
      setSelectedMethod(method);
      setError(null);

      // PayPal: skip Braintree init, set READY
      if (method === 'paypal') {
        setStatus(PAYMENT_STATUS.READY);
        return;
      }

      setStatus(PAYMENT_STATUS.INITIALIZING);

      try {
        const type = flow === 'recurring' || flow === 'activation' ? 'recurring' : 'onetime';
        const braintreeClient = await initBraintree({ type, method, environment: BRAINTREE_ENV });
        clientRef.current = braintreeClient;

        // DEV TESTING DEBUG
        // try {
        //   const hasConfig = !!braintreeClient?.getConfiguration?.();
        //   console.log('[Braintree client config] present:', hasConfig);
        // } catch (cfgErr) {
        //   console.warn('[Braintree client config] could not check configuration presence');
        // }

        // Google Pay initialization (lazy: only when user selects it)
        // Re-initializes each time in case environment or method changed
        if (method === 'googlepay') {
          // console.log('[GooglePay] Initializing for payment...');
          // DEV TESTING DEBUG: Log env merchant ID
          // try {
          //   console.log('[GooglePay] *** G MERCHANT_ID:', GOOGLE_PAY_MERCHANT_ID);
          // } catch (e) {
          //   // Silently ignore if env var is not set
          // }

          // Create or reuse PaymentsClient for this environment
          if (!paymentsClientRef.current) {
            try {
              paymentsClientRef.current = new window.google.payments.api.PaymentsClient({
                environment: GOOGLE_PAY_ENV
              });
              // console.log('[GooglePay] PaymentsClient created for environment:', GOOGLE_PAY_ENV);
              // console.log('[GooglePay] PaymentsClient initialized successfully');
            } catch (err) {
              throw new Error('Failed to initialize Google Pay: PaymentsClient creation failed');
            }
          }

          // Initialize Braintree Google Payment module
          // This module handles tokenization with Braintree's nonce generation
          if (!googlePayInstanceRef.current) {
            try {
              // REMOVE - Google Pay debug logging
              // console.log(
              //   '🔍 [GooglePay] Creating Braintree instance with merchant ID:',
              //   GOOGLE_PAY_MERCHANT_ID
              // );
              // console.log('🔍 [GooglePay] Environment:', GOOGLE_PAY_ENV);
              // console.log('🔍 [GooglePay] Braintree Environment:', BRAINTREE_ENV);

              googlePayInstanceRef.current = await googlePayment.create({
                client: braintreeClient,
                googlePayVersion: 2,
                googleMerchantId: GOOGLE_PAY_MERCHANT_ID
              });

              // REMOVE - Log configuration details
              // console.log('🔍 [GooglePay] Instance created successfully');
              
              try {
                const fullGw = braintreeClient.getConfiguration?.().gatewayConfiguration;
                // console.log('🔍 [GooglePay] Braintree gatewayConfiguration:', fullGw);
              } catch (gwErr) {
                console.warn('[GooglePay] Could not read full gatewayConfiguration');
              }
            } catch (err) {
              throw new Error(`Failed to initialize Braintree Google Payment: ${err.message}`);
            }
          }
        }

        if (method === 'applepay') {
          // console.log('[ApplePay] Initializing for payment...');

          try {
            const fullConfig = braintreeClient.getConfiguration?.();
            const appleCfg = fullConfig?.gatewayConfiguration?.applePayWeb;

            // console.log('[ApplePay] Braintree Environment:', fullConfig?.environment);
            // console.log('[ApplePay] Client Token present:', !!fullConfig?.authorizationFingerprint);
            // console.log('[ApplePay] applePayWeb enabled:', !!appleCfg);

            if (appleCfg) {
              // console.log('[ApplePay] applePayWeb config:', {
              //   merchantId: appleCfg.merchantId,
              //   displayName: appleCfg.displayName,
              //   supportedNetworks: appleCfg.supportedNetworks
              // });
            } else {
              // console.error('[ApplePay] CRITICAL - applePayWeb config is MISSING');
              // console.error(
              //   '[ApplePay] Action: Check Braintree Control Panel → Apple Pay settings'
              // );
              // console.error(
              //   '[ApplePay] Action: Verify merchant ID is configured for PRODUCTION environment'
              // );
            }
          } catch (appleCfgErr) {
            // console.warn(
            //   '[ApplePay] Could not read applePay gateway configuration:',
            //   appleCfgErr.message
            // );
          }

          if (!applePayInstanceRef.current) {
            try {
              applePayInstanceRef.current = await applePay.create({
                client: braintreeClient
              });
              // console.log('[ApplePay] Braintree applePay module initialized successfully');
            } catch (err) {
              throw new Error(`Failed to initialize Braintree Apple Pay: ${err.message}`);
            }
          }
        }

        if (isMountedRef.current) setStatus(PAYMENT_STATUS.READY);
      } catch (err) {
        if (isMountedRef.current) {
          setError(err.message);
          setStatus(PAYMENT_STATUS.ERROR);
        }
      }
    },
    [flow, initBraintree]
  );

  // ----------------------------
  // Hosted card fields
  // ----------------------------
  const initializeCardFields = useCallback(async (fieldOptions = {}) => {
    const activeClient = clientRef.current;
    if (!activeClient) throw new Error('Braintree client not initialized');

    if (hostedFieldsRef.current) await teardownHostedFields(hostedFieldsRef.current);

    const fields = await createHostedFields(activeClient, { ...fieldOptions, t: tA11yTranslate });
    hostedFieldsRef.current = fields;

    setupFieldListeners(fields, {
      onValidityChange: (event) => {
        if (isMountedRef.current) {
          setFieldValidity((prev) => ({
            ...prev,
            [event.emittedBy]: event.fields[event.emittedBy].isValid
          }));
        }
      }
    });

    return fields;
  }, []);

  // NEW after splitting credit card into two subtypes
  const isCardFormValid = useCallback(() => {
    if (!hostedFieldsRef.current) return false;

    // Guard against calling methods on torn-down instance
    try {
      return validateAllFields(hostedFieldsRef.current).isValid;
    } catch (err) {
      if (err.code === 'METHOD_CALLED_AFTER_TEARDOWN') {
        // console.debug('[isCardFormValid] Hosted Fields already torn down, return false');
        hostedFieldsRef.current = null;
        return false;
      }
      throw err;
    }
  }, []);

  // ----------------------------
  // PayPal payment handler
  // ----------------------------
  const handlePaypalPayment = useCallback(
    async ({ amount, msisdn, email, unauthorized }) => {
      setStatus(PAYMENT_STATUS.PROCESSING);
      setError(null);

      // Choose API method based on flow
      const apiMethod =
        flow === 'recurring' || flow === 'activation'
          ? paymentService.registerRecurringPayment
          : paymentService.processOneTimePayment;

      // Call backend for PayPal redirect
      // console.log('[handlePaypalPayment] TRACE - flow:', flow);

      const paypalPayload = {
        amount,
        method: 'paypal',
        msisdn,
        // Public online topup: always include email and unauthorized
        ...(flow === 'public-online-topup'
          ? { email, unauthorized: true }
          : {
              ...(email ? { email } : {}),
              // Add unauthorized flag for activation flow OR if explicitly passed (public onetime flows)
              ...(flow === 'activation' || unauthorized ? { unauthorized: true } : {})
            })
        // returnUrl, cancelUrl, errorUrl - coordinated with backend separately
      };
      // console.log('[usePaymentFlow] PayPal initiation payload:', paypalPayload);

      const result = await apiMethod.call(paymentService, paypalPayload);

      if (result.lookup_key) {
        sessionStorage.setItem('mps_lookup_key', result.lookup_key);
      }

      // Persist payment method for feedback page
      const actualMethod = result.method || 'paypal';
      sessionStorage.setItem('mps_payment_method', actualMethod);

      // Persist payment flow for PaymentCallbackHandler (only if not already set by caller)
      // The page that initiates payment sets mps_payment_flow accurately (e.g., 'public-online-topup' vs 'onetime')
      // Only set it here for PayPal if not already set
      if (!sessionStorage.getItem('mps_payment_flow')) {
        sessionStorage.setItem('mps_payment_flow', flow);
      }
      sessionStorage.setItem('mps_payment_amount', String(amount));

      if (result.requiresRedirect && result.redirectUrl) {
        setStatus(PAYMENT_STATUS.REDIRECTING);

        // console.log('🔵 PayPal redirect starting...');
        // console.log('flow:', flow);
        // console.log('amount:', amount);
        // console.log('redirectUrl:', result.redirectUrl);

        // console.log('🟢 Saved to sessionStorage:');
        // console.log('mps_payment_flow:', sessionStorage.getItem('mps_payment_flow'));
        // console.log('mps_payment_amount:', sessionStorage.getItem('mps_payment_amount'));
        // console.log('mps_payment_method:', sessionStorage.getItem('mps_payment_method'));

        window.location.href = result.redirectUrl;
        return result;
      }

      // Handle returned terminal states explicitly: error or cancelled
      if (result?.error) {
        setPaymentResult(result);
        setStatus(PAYMENT_STATUS.ERROR);
        onError?.(result);
        return result;
      }

      if (result?.cancelled) {
        setPaymentResult(result);
        setStatus(PAYMENT_STATUS.ERROR);
        onError?.(result);
        return result;
      }

      setPaymentResult(result);
      setStatus(PAYMENT_STATUS.SUCCESS);
      onSuccess?.(result);
      return result;
    },
    [flow, onSuccess]
  );

  // ----------------------------
  // Payment processor
  // ----------------------------
  const processPayment = useCallback(
    async ({ amount, currency = 'EUR', msisdn, useStored = false, email, unauthorized }) => {
      if (!selectedMethod) throw new Error('No payment method selected');
      setStatus(PAYMENT_STATUS.PROCESSING);
      setError(null);

      // console.log('[processPayment] method:', selectedMethod);
      // console.log('[processPayment] isCardFormValid:', isCardFormValid());
      // console.log('[processPayment] hostedFieldsRef:', hostedFieldsRef.current);
      // console.log('[processPayment] extra params:', { email, unauthorized });

      try {
        let nonce = null;

        // If using stored payment token, skip tokenization/3DS/nonce flows
        if (useStored) {
          // console.log('[processPayment] Using stored payment. Skipping nonce/tokenization.');
        }

        // PayPal redirect flow
        if (selectedMethod === 'paypal') {
          return handlePaypalPayment({ amount, msisdn, email, unauthorized });
        }

        // Hosted fields (card) with 3D Secure verification
        if (!useStored && selectedMethod === 'creditcard' && hostedFieldsRef.current) {
          // console.log('HostedFieldsRef before tokenize:', hostedFieldsRef.current);
          setStatus(PAYMENT_STATUS.VALIDATING);
          if (!isCardFormValid()) throw new Error('Fill in card details correctly');
          // console.log('[Tokenize] Starting...');

          const { nonce: tokenizedNonce, bin } = await tokenizeHostedFields(
            hostedFieldsRef.current
          );

          // console.log('[Tokenize] tokenizeHostedFields nonce received');
          // console.log('[Tokenize] Card BIN extracted:' || '(not in payload)');

          // Verify card with 3D Secure using Braintree SDK (frontend handles 3DS)
          if (!clientRef.current) {
            throw new Error('Braintree client not initialized for 3DS');
          }

          try {
            // console.log('[3DS] Starting verification...');
            if (bin) {
              // console.log('[3DS] Using BIN:', bin);
            }
            nonce = await threeDS.verify3DSCard(
              clientRef.current,
              tokenizedNonce,
              amount,
              msisdn,
              bin
            );
            // console.log('[3DS] Verification successful');
            setStatus(PAYMENT_STATUS.PROCESSING);
          } catch (err) {
            // Check if this is a user cancellation
            if (err?.cancelled) {
              // console.log('[3DS] User cancelled - not setting error state');
              setStatus(PAYMENT_STATUS.READY); // Reset to ready so user can try again
              throw err; // Still throw so component can handle it
            }

            // For actual errors, set error state
            // console.error('[3DS] Verification failed:', err.message);
            setStatus(PAYMENT_STATUS.ERROR);
            setError(err.message);
            throw err;
          }
        }

        // Google Pay v2 payment flow
        // ========================================
        // Two-Phase Configuration Architecture:
        //
        // PHASE 1 (detectGooglePay effect):
        //   - Uses GOOGLE_PAY_BASE_REQUEST constant (hardcoded values)
        //   - Fast device capability check via isReadyToPay()
        //   - Runs early in component lifecycle before Braintree init
        //   - Purpose: Quick signal to show/hide Google Pay UI button
        //   - Trade-off: May not reflect merchant's specific capabilities
        //
        // PHASE 2 (this processPayment function):
        //   - Uses googlePayInstance.createPaymentDataRequest() (Braintree config)
        //   - Merchant-specific authoritative configuration
        //   - Runs when user selects Google Pay payment method
        //   - Purpose: Actual payment processing with correct merchant config
        //   - Guarantee: Respects what backend can actually process
        //
        // Why two phases?
        //   - Phase 1 must run before Braintree init (faster, shows UI)
        //   - Phase 2 uses real Braintree config (slower init, accurate processing)
        //   - If there's mismatch, Phase 2 is authoritative (see dev warnings below)
        // ========================================
        else if (selectedMethod === 'googlepay') {
          const executionId = Math.random().toString(36).substring(7);
          // console.log(`[GooglePay] Starting payment flow with ID: ${executionId}`);
          // console.log('[GooglePay] Processing payment...');
          // console.log('[GooglePay] isMountedRef.current?', isMountedRef.current);
          const googlePayInstance = googlePayInstanceRef.current;
          const paymentsClient = paymentsClientRef.current;

          // DEV TESTING DEBUG
          // console.log(`[GooglePay-${executionId}] Instance methods available:`, {
          //   hasCreatePaymentDataRequest: typeof googlePayInstance?.createPaymentDataRequest,
          //   hasParseResponse: typeof googlePayInstance?.parseResponse
          // });

          // Ensure Google Pay is properly initialized
          if (!googlePayInstance) {
            throw new Error(
              'Google Pay not initialized. Please try again or select a different payment method.'
            );
          }
          if (!paymentsClient) {
            throw new Error(
              'Google Pay PaymentsClient not available. Please try again or select a different payment method.'
            );
          }

          try {
            // PHASE 2: Authoritative Signal - Braintree Configuration
            // Now that Braintree is initialized, use its authoritative config
            // createPaymentDataRequest() returns the merchant-specific configuration
            // This reflects the actual merchant settings and payment processor capabilities

            // REMOVE after testing
            // Override merchantInfo with explicit merchantName and merchantId from env to
            // work around missing server-side googlePayWeb configuration.
            // const overriddenMerchantInfo = {
            //   merchantId: GOOGLE_PAY_MERCHANT_ID
            // };
            // console.log(
            //   '[GooglePay] Overriding merchantInfo for createPaymentDataRequest:',
            //   overriddenMerchantInfo
            // );

            // DEV TESTING DEBUG: Log the instance reference and check if it matches the ref (should be same)
            // console.log(
            //   '[GooglePay] is current instance same?',
            //   googlePayInstance === googlePayInstanceRef.current
            // );
            const paymentDataRequest = googlePayInstance.createPaymentDataRequest({
              // merchantInfo: overriddenMerchantInfo,
              transactionInfo: {
                totalPriceStatus: 'FINAL',
                totalPrice: String(amount),
                currencyCode: currency
              }
            });

            // REMOVE - Log payment data request
            // console.log('🔍 [GooglePay] Payment Data Request created:', {
            //   environment: GOOGLE_PAY_ENV,
            //   merchantId: GOOGLE_PAY_MERCHANT_ID,
            //   amount: String(amount),
            //   currency,
            //   merchantInfo: paymentDataRequest?.merchantInfo,
            //   allowedPaymentMethods: paymentDataRequest?.allowedPaymentMethods,
            //   tokenizationSpec: paymentDataRequest?.allowedPaymentMethods?.[0]?.tokenizationSpecification,
            //   transactionInfo: paymentDataRequest?.transactionInfo
            // });
            // console.log('🔍 [GooglePay] FULL paymentDataRequest object:', paymentDataRequest);

            // NOTE NEW: 2026-5-8 - is this necessary?
            // Braintree developer says "probably not" so it will stay commented for now, but leaving here for reference if we want to add it in the future
            // Per Braintree best practices: collect billing address information
            // Required for enhanced validation and fraud prevention on card payments
            // See: https://developers.google.com/pay/api/web/reference/object
            // try {
            //   const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0];
            //   cardPaymentMethod.parameters.billingAddressRequired = true;
            //   cardPaymentMethod.parameters.billingAddressParameters = {
            //     format: 'FULL',
            //     phoneNumberRequired: true
            //   };
            //   console.log('[GooglePay] Billing address parameters added:', {
            //     billingAddressRequired: cardPaymentMethod.parameters.billingAddressRequired,
            //     billingAddressParameters: cardPaymentMethod.parameters.billingAddressParameters
            //   });
            //   console.log('[GooglePay] Updated cardPaymentMethod:', cardPaymentMethod);
            // } catch (billingErr) {
            //   console.warn('[GooglePay] Could not set billing address parameters:', billingErr?.message);
            // }

            // DEV TESTING DEBUG: Log the paymentDataRequest before showing the sheet
            // console.log('[GooglePay] paymentDataRequest created:', paymentDataRequest);

            // DEV: Validate Phase 1 vs Phase 2 alignment (optional for debugging)
            // In development, check if Braintree config differs from our constant
            // If they differ, log a warning but use Braintree's config (it's authoritative)
            if (process.env.NODE_ENV === 'development') {
              const braintreeAllowedNetworks =
                paymentDataRequest.allowedPaymentMethods?.[0]?.parameters?.allowedCardNetworks;
              const constantAllowedNetworks =
                GOOGLE_PAY_BASE_REQUEST.allowedPaymentMethods[0].parameters.allowedCardNetworks;
              if (
                braintreeAllowedNetworks &&
                JSON.stringify(braintreeAllowedNetworks.sort()) !==
                  JSON.stringify(constantAllowedNetworks.sort())
              ) {
                // console.warn('[GooglePay] Configuration mismatch between Phase 1 and Phase 2:');
                // console.warn('[GooglePay] Phase 1 (constant):', constantAllowedNetworks);
                // console.warn('[GooglePay] Phase 2 (Braintree):', braintreeAllowedNetworks);
                // console.warn('[GooglePay] Using Braintree config as it is authoritative');
              }
            }

            // console.log('[GooglePay] Showing payment sheet for amount:', amount, currency);

            // DEV TESTING DEBUG: log full paymentDataRequest
            // try {
            //   console.log('[GooglePay] *** REACT_APP_GOOGLE_PAY_ENV:', GOOGLE_PAY_ENV);
            //   console.log(
            //     '[GooglePay] *** ENV MERCHANT_ID from process.env:',
            //     GOOGLE_PAY_MERCHANT_ID
            //   );
            //   console.log(
            //     '[GooglePay] *** paymentDataRequest.merchantInfo:',
            //     paymentDataRequest?.merchantInfo
            //   );
            //   console.log(
            //     '[GooglePay] *** tokenization parameters:',
            //     paymentDataRequest?.allowedPaymentMethods?.[0]?.tokenizationSpecification
            //       ?.parameters
            //   );
            //   console.log('[GooglePay] *** FULL paymentDataRequest object:', paymentDataRequest);
            // } catch (logErr) {
            //   console.warn(
            //     '[GooglePay] Could not log paymentDataRequest parts:',
            //     logErr?.message || logErr
            //   );
            // }

            // PHASE 2 CONTINUED: Present payment sheet with authoritative configuration
            // The paymentDataRequest uses Braintree's createPaymentDataRequest() which:
            // - Respects merchant capabilities in Braintree account
            // - Includes proper merchant configuration (merchant ID, business name, etc.)
            // - Ensures payment method constraints match what backend can process
            // User selects a payment method (saved card or new card) from Google Pay sheet

            // DEV TESTING DEBUG: Final check: verify merchantInfo before loading payment data
            // console.log(
            //   '[GooglePay] Final paymentDataRequest.merchantInfo:',
            //   paymentDataRequest?.merchantInfo
            // );
            // Verify PaymentsClient before calling loadPaymentData
            // console.log(
            //   `[GooglePay-${executionId}] paymentsClient valid?`,
            //   !!paymentsClient,
            //   'type:',
            //   typeof paymentsClient
            // );
            // console.log(
            //   `[GooglePay-${executionId}] paymentsClient.loadPaymentData?`,
            //   typeof paymentsClient?.loadPaymentData
            // );

            let paymentData;
            try {
              // REMOVE - Log before loading payment data
              // console.log('🔍 [GooglePay] About to call loadPaymentData (showing payment sheet)');
              // console.log('🔍 [GooglePay] PaymentsClient valid:', !!paymentsClient);

              paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);
              
              // REMOVE - Log successful payment data
              // console.log('🔍 [GooglePay] loadPaymentData successful - User completed payment sheet');
              // console.log('🔍 [GooglePay] Payment data received:', paymentData);
              // console.log('🔍 [GooglePay] Payment method data:', paymentData?.paymentMethodData);
              
            } catch (err) {
              // If user cancels the Google Pay sheet, return to method selection (like PayPal)
              if (err.statusCode === 'CANCELED') {
                // console.log('[GooglePay] User cancelled payment sheet, returning to method selection');
                setStatus(PAYMENT_STATUS.READY);
                setSelectedMethod(null);
                return { cancelled: true };
              }
              
              // REMOVE - Log error details
              // console.error('❌ [GooglePay] loadPaymentData failed:', {
              //   statusCode: err?.statusCode,
              //   message: err?.message
              // });
              
              // if (err?.details) {
              //   console.error('❌ [GooglePay] Error details:', err.details);
              // }
              
              throw new Error(`Google Pay transaction failed: ${err.message}`);
            }

            // PHASE 2 COMPLETION: Tokenize with Braintree
            // Convert Google Pay's encrypted payment token to Braintree nonce format
            // This nonce can then be sent to the backend for processing
            // Braintree's parseResponse() handles:
            // - Token decryption (if needed)
            // - Format conversion to Braintree nonce
            // - Validation of payment method against merchant config
            try {
              // REMOVE - Log tokenization
              // console.log('🔍 [GooglePay] About to parseResponse (tokenization with Braintree)');
              
              const result = await googlePayInstance.parseResponse(paymentData);
              nonce = result.nonce;
              
              // REMOVE - Log successful tokenization
              // console.log('🔍 [GooglePay] parseResponse completed successfully');
              // console.log('🔍 [GooglePay] Nonce generated:', nonce);
              // console.log('🔍 [GooglePay] Full tokenization result:', result);
              
            } catch (err) {
              // console.error('❌ [GooglePay] parseResponse failed:', err?.message || err);
              throw new Error(`Failed to tokenize Google Pay payment: ${err.message}`);
            }
          } catch (err) {
            setStatus(PAYMENT_STATUS.ERROR);
            setError(err.message);
            throw err;
          }
        }

        // Apple Pay (similar two-phase architecture as Google Pay)
        else if (selectedMethod === 'applepay') {
          // console.log('[ApplePay] Processing payment...');
          const applePayInstance = applePayInstanceRef.current;
          if (!applePayInstance) {
            throw new Error(
              'Apple Pay not initialized. Please try again or select a different payment method.'
            );
          }

          try {
            // Create payment request with Braintree configuration
            const paymentRequest = applePayInstance.createPaymentRequest({
              total: {
                label: APPLE_PAY_MERCHANT_LABEL,
                amount: String(amount)
              },
              currencyCode: currency || APPLE_PAY_CURRENCY,
              countryCode: APPLE_PAY_COUNTRY_CODE
            });

            // console.log('[ApplePay] Payment request created:', {
            //   amount,
            //   currency: currency || APPLE_PAY_CURRENCY,
            //   merchant: APPLE_PAY_MERCHANT_LABEL
            // });

            // CRITICAL: Check iframe context before creating session
            const inIframe = isRunningInIframe();
            if (inIframe) {
              console.warn('[ApplePay] CREATING SESSION IN IFRAME CONTEXT');
              console.warn('[ApplePay] This may fail due to cross-origin restrictions');
            }

            nonce = await new Promise((resolve, reject) => {
              // console.log('[ApplePay] Creating ApplePaySession (version 3)...');

              let session;
              try {
                session = new window.ApplePaySession(3, paymentRequest);
                // console.log('[ApplePay] Session created successfully');
              } catch (sessionErr) {
                // console.error('[ApplePay] Failed to create session:', sessionErr?.message);
                reject(new Error(`Failed to create Apple Pay session: ${sessionErr.message}`));
                return;
              }

              // Track if any event fired
              let eventsFired = {
                validatemerchant: false,
                paymentauthorized: false,
                cancel: false
              };

              // Merchant validation - required by Apple Pay
              session.onvalidatemerchant = async (event) => {
                eventsFired.validatemerchant = true;
                // console.log('[ApplePay] onvalidatemerchant event fired');
                try {
                  // console.log('[ApplePay] Validating merchant...');
                  // console.log('[ApplePay] Validation URL:', event.validationURL);
                  // console.log('[ApplePay] Display Name:', APPLE_PAY_MERCHANT_LABEL);
                  // console.log('[ApplePay] performValidation request:', {
                  //   validationURL: event.validationURL,
                  //   displayName: APPLE_PAY_MERCHANT_LABEL
                  // });

                  const validationData = await applePayInstance.performValidation({
                    validationURL: event.validationURL,
                    displayName: APPLE_PAY_MERCHANT_LABEL
                  });

                  // console.log('[ApplePay] Validation response:', validationData);
                  session.completeMerchantValidation(validationData);
                  // console.log('[ApplePay] Merchant validated successfully');
                } catch (err) {
                  // console.error('[ApplePay] Merchant validation failed:', {
                  //   message: err.message,
                  //   status: err.status
                  // });

                  session.abort();
                  reject(new Error(`Apple Pay merchant validation failed: ${err.message}`));
                }
              };

              // User authorized the payment
              session.onpaymentauthorized = async (event) => {
                eventsFired.paymentauthorized = true;
                // console.log('[ApplePay] onpaymentauthorized event fired');
                try {
                  // console.log('[ApplePay] User authorized payment, tokenizing...');
                  const payload = await applePayInstance.tokenize({ token: event.payment.token });
                  session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
                  // console.log('[ApplePay] Successfully tokenized, nonce ready for backend');
                  resolve(payload.nonce);
                } catch (err) {
                  // console.error('[ApplePay] Tokenization failed:', err.message);
                  session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                  reject(new Error('Apple Pay tokenization failed'));
                }
              };

              // User cancelled the payment sheet
              session.oncancel = (event) => {
                eventsFired.cancel = true;
                // console.log('[ApplePay] oncancel event fired');
                // console.log('[ApplePay] Cancel event details:', event);
                // console.log('[ApplePay] Events fired before cancel:', eventsFired);

                // If cancel fired immediately without validatemerchant, likely iframe issue
                if (!eventsFired.validatemerchant) {
                  const inIframe = window.self !== window.top;
                  if (inIframe) {
                    console.error('[ApplePay] CONFIRMED: Running in iframe');
                  }
                }

                // console.log('[ApplePay] User cancelled payment sheet');
                // Return cancelled state like Google Pay (graceful return to method selection)
                resolve({ cancelled: true });
              };

              // console.log('[ApplePay] Event handlers attached, calling session.begin()...');
              // console.log('[ApplePay] Timestamp before begin():', new Date().toISOString());

              try {
                session.begin();
                // console.log('[ApplePay] session.begin() called successfully');
                // console.log('[ApplePay] Timestamp after begin():', new Date().toISOString());
                // console.log('[ApplePay] Waiting for user interaction with payment sheet...');
              } catch (beginErr) {
                // console.error('[ApplePay] session.begin() threw error:', beginErr?.message);
                // console.error('[ApplePay] Error type:', beginErr.name);
                // console.error('[ApplePay] Error message:', beginErr.message);
                reject(new Error(`Failed to show Apple Pay sheet: ${beginErr.message}`));
              }
            });

            // Handle cancellation response
            if (nonce?.cancelled === true) {
              // console.log('[ApplePay] User cancelled, returning to method selection');
              setStatus(PAYMENT_STATUS.READY);
              setSelectedMethod(null);
              return { cancelled: true };
            }

            // console.log('[ApplePay] Payment flow completed successfully, nonce obtained');
          } catch (err) {
            // console.error('[ApplePay] Payment error:', err.message);

            // Check if this is an iframe-related error
            const inIframe = isRunningInIframe();
            if (inIframe) {
              console.error('[ApplePay] Error occurred in iframe context');
            }

            setStatus(PAYMENT_STATUS.ERROR);
            setError(err.message);
            throw err;
          }
        }

        // Send to backend
        const apiMethod =
          flow === 'recurring' || flow === 'activation'
            ? paymentService.registerRecurringPayment
            : paymentService.processOneTimePayment;

        // If using stored payment, backend expects method 'stored' and no nonce
        const methodForBackend = useStored ? 'stored' : selectedMethod;

        // DEBUG: Trace flow and unauthorized flag
        // console.log('[processPayment] TRACE - flow value:', flow);
        // console.log(
        //   '[processPayment] TRACE - will include unauthorized?',
        //   flow === 'activation' || unauthorized
        // );
        // console.log(
        //   '[processPayment] TRACE - isMountedRef.current before backend call?',
        //   isMountedRef.current
        // );

        const backendPayload = {
          amount,
          method: methodForBackend,
          ...(nonce ? { nonce } : {}),
          msisdn,
          // Public online topup: always include email and unauthorized
          ...(flow === 'public-online-topup'
            ? { email, unauthorized: true }
            : {
                ...(email ? { email } : {}),
                // Add unauthorized flag for activation flow OR if explicitly passed (public onetime flows)
                ...(flow === 'activation' || unauthorized ? { unauthorized: true } : {})
              })
        };
        // Do not log sensitive fields (nonce). Mask or omit before logging.
        try {
          const safePayload = { ...backendPayload };
          if (safePayload.nonce) safePayload.nonce = '***REDACTED***';
          // console.log('[processPayment] TRACE - final backendPayload before service:', safePayload);
          // console.log('[processPayment] Sending to backend (nonce redacted)');
        } catch (logErr) {
          // console.warn('[processPayment] Could not log backend payload safely');
        }
        // console.log('[processPayment] About to call backend API');
        const result = await apiMethod.call(paymentService, backendPayload);
        // console.log('[processPayment] Backend response:', result);
        // console.log(
        //   '[processPayment] isMountedRef.current after backend call?',
        //   isMountedRef.current
        // );

        // store lookup_key for result polling
        if (result.lookup_key) {
          sessionStorage.setItem('mps_lookup_key', result.lookup_key);
        }

        // Persist payment method for feedback page
        const actualMethod = result.method || selectedMethod;
        sessionStorage.setItem('mps_payment_method', actualMethod);

        // Persist payment flow for PaymentCallbackHandler (for ALL payment methods, not just redirects)
        if (!sessionStorage.getItem('mps_payment_flow')) {
          sessionStorage.setItem('mps_payment_flow', flow);
        }
        sessionStorage.setItem('mps_payment_amount', String(amount));
        // console.log(
        //   '[processPayment] Session storage values set, isMountedRef.current?',
        //   isMountedRef.current
        // );

        if (result.requiresRedirect && result.redirectUrl) {
          // console.log('[processPayment] Setting status to REDIRECTING');
          setStatus(PAYMENT_STATUS.REDIRECTING);
          window.location.href = result.redirectUrl;
          return result;
        }

        // Handle returned terminal states explicitly: error or cancelled
        if (result?.error) {
          // console.log('[processPayment] Setting status to ERROR (result.error)');
          setPaymentResult(result);
          setStatus(PAYMENT_STATUS.ERROR);
          onError?.(result);
          return result;
        }

        if (result?.cancelled) {
          // console.log('[processPayment] Setting status to ERROR (result.cancelled)');
          setPaymentResult(result);
          setStatus(PAYMENT_STATUS.ERROR);
          onError?.(result);
          return result;
        }

        // console.log('[processPayment] Setting status to SUCCESS');
        setPaymentResult(result);
        setStatus(PAYMENT_STATUS.SUCCESS);
        onSuccess?.(result);
        return result;
      } catch (err) {
        if (isMountedRef.current) {
          // Check if this is a user cancellation (3DS, Apple Pay, Google Pay)
          if (err?.cancelled) {
            // console.log('[processPayment] User cancelled - not setting error state');
            setStatus(PAYMENT_STATUS.READY); // Reset to ready so user can try again
            throw err; // Still throw so component can handle it
          }

          // For actual errors, set error state
          setError(err.message);
          setStatus(PAYMENT_STATUS.ERROR);
          onError?.(err);
        }
        throw err;
      }
    },
    [selectedMethod, flow, isCardFormValid, onSuccess, onError, hostedFieldsRef]
  );

  // ----------------------------
  // Reset payment flow
  // ----------------------------
  const reset = useCallback(async () => {
    if (hostedFieldsRef.current) {
      await teardownHostedFields(hostedFieldsRef.current);
      hostedFieldsRef.current = null;
    }
    cleanupBraintree();
    googlePayInstanceRef.current = null;
    paymentsClientRef.current = null;
    applePayInstanceRef.current = null; // Cleanup Apple Pay instance
    setStatus(PAYMENT_STATUS.IDLE);
    setError(null);
    setPaymentResult(null);
    setSelectedMethod(null);
    setFieldValidity({ number: false, cvv: false, expirationDate: false });
  }, [cleanupBraintree]);

  const cardFormValid = isCardFormValid();

  return {
    status,
    error,
    paymentResult,
    selectedMethod,
    setSelectedMethod,
    fieldValidity,
    isReady: status === PAYMENT_STATUS.READY,
    isProcessing: status === PAYMENT_STATUS.PROCESSING || status === PAYMENT_STATUS.VALIDATING,
    isSuccess: status === PAYMENT_STATUS.SUCCESS,
    isError: status === PAYMENT_STATUS.ERROR,
    isCardFormValid: cardFormValid,
    isBraintreeMethod: selectedMethod ? paymentService.isBraintreeMethod(selectedMethod) : false,
    selectPaymentMethod,
    initializeCardFields,
    processPayment,
    // handlePaymentCallback,
    reset,
    hostedFieldsRef,
    canUseApplePay,
    canUseGooglePay,
    paymentsClientRef: paymentsClientRef.current
  };
}

export default usePaymentFlow;
